import { globalEmitter, emitterProps } from '@/components/helpers/emitter-event'
import { swymService } from 'libs/swym/swym-service'
import { SIZE_SELECTED_CPROPS, isSizeSelected } from 'libs/swym/swym-entry'

/**
 * PDP wishlist toggle. Reads the selected variant from the product form's
 * `[name="id"]` field (a hidden `<input>` for single-variant products, a
 * `<select>` for the grid/universal size selectors — both expose the current
 * value via `.value`). Falls back to the sticky ATC bar's own `[name="id"]`
 * (via `data-pair-form-id`) when the main form has no value selected — see
 * `selectedVariantId()`, since variant-selector.ts only mirrors main → sticky,
 * never the other way.
 *
 * Saving is size-specific: with no size selected the heart shows product-level
 * state (any saved size) and a click either clears every saved size or, when
 * nothing is saved, shows the same "Please select size above" warning the ATC
 * button shows. With a size selected the heart reflects and toggles that size
 * only, so a shopper can save several sizes of one style. Saving a size also
 * clears any product-level entry left over from a PLP heart (`onClick`), so a
 * product never shows both a generic card and a size-specific one.
 */
export class WishlistToggle extends HTMLElement {
  private button: HTMLButtonElement | null = null
  private busy = false
  // Cached separately from formHost()'s per-call lookup: a partial AJAX
  // re-render (see helpers/section-render.ts) can swap in a new form host
  // node while this element stays connected. The `change` listener must be
  // added to and removed from the same node, so the reference is resolved
  // once here rather than re-resolved (possibly to a different node) in
  // disconnectedCallback.
  private formHostElement: HTMLElement | null = null

  private get productId(): number {
    return Number(this.dataset.productId)
  }

  connectedCallback(): void {
    this.button = this.querySelector('[data-wishlist-toggle]')
    this.button?.addEventListener('click', this.onClick)
    this.formHostElement = this.formHost()
    // `change` bubbles from the form's `[name="id"]` select — both the native
    // event and the synthetic one variant-selector.ts dispatches on selection.
    this.formHostElement?.addEventListener('change', this.onFormChange)
    globalEmitter.on(emitterProps.wishlist.change, this.syncSaved)
    this.syncSaved()
  }

  disconnectedCallback(): void {
    this.button?.removeEventListener('click', this.onClick)
    this.formHostElement?.removeEventListener('change', this.onFormChange)
    this.formHostElement = null
    globalEmitter.off(emitterProps.wishlist.change, this.syncSaved)
  }

  private onClick = async (event: Event): Promise<void> => {
    event.stopPropagation()
    if (this.busy) return

    const variantId = this.selectedVariantId()
    const saved = this.isSaved()

    // No size chosen: product-level view. Nothing saved yet means there is
    // nothing to toggle, so prompt for a size instead of guessing one.
    if (!variantId) {
      if (!saved) {
        this.showSizePrompt()
        return
      }
      this.busy = true
      const removed = await swymService.removeProduct(this.productId)
      if (removed) this.hideStatus()
      else this.showStatus('error')
      this.busy = false
      return
    }

    this.busy = true
    // `cprops` is what marks this as a size the shopper chose: every entry stores
    // a real `epi`, including product-level PLP saves that defaulted to one.
    const payload = {
      epi: variantId,
      empi: this.productId,
      du: this.productUrl(),
      cprops: SIZE_SELECTED_CPROPS
    }
    if (saved) {
      const removed = await swymService.removeVariant(payload)
      if (removed) this.hideStatus()
      else this.showStatus('error')
    } else {
      const added = await swymService.add(payload)
      this.showStatus(added ? 'success' : 'error')
      // The save already succeeded (or didn't) above, so this cleanup never
      // changes the status shown to the shopper — it's just tidying up a
      // now-redundant product-level entry (e.g. a PLP heart) for this size.
      if (added) await this.clearOtherProductLevelEntries(variantId)
    }
    this.busy = false
  }

  /**
   * A size-specific save should leave exactly one card per product: the
   * other entry for this product that carries no `cprops` (a product-level
   * save — a PLP heart, or anything saved before this feature shipped) is
   * superseded by the size just chosen, so it goes — but only when there is
   * exactly one such entry.
   *
   * Guard: run only when the product has exactly one cprops-less entry
   * (excluding the entry just saved). Two or more cprops-less entries for one
   * product can't arise from normal use — the PLP heart removes rather than
   * adding a second, and every entry saved before this feature was one per
   * product — so 2+ is a signal that `cprops` was dropped somewhere (e.g. by
   * Swym's guest→login list merge, which this feature has never observed
   * end to end — see the spec's Risks section) rather than evidence that the
   * shopper made several genuine product-level saves. Deleting in that case
   * would silently discard saved sizes, which is exactly the loss this guard
   * exists to prevent, so the safe response is to leave them alone.
   *
   * The `epi !== variantId` exclusion is defence-in-depth, not load-bearing:
   * in the `getList()` hydration race, the entry just saved always ends up
   * flagged as size-selected either way (the server echoes `cprops` back, or
   * `add()`'s merge path in swym-service.ts tags the cached entry itself), so
   * `isSizeSelected(entry)` alone would already exclude it. Kept so a future
   * change to that merge path can't reintroduce self-deletion unnoticed.
   */
  private async clearOtherProductLevelEntries(variantId: number): Promise<void> {
    const stale = swymService
      .entriesFor(this.productId)
      .filter((entry) => !isSizeSelected(entry) && Number(entry.epi) !== variantId)

    if (stale.length !== 1) return

    await swymService.removeVariant(stale[0])
  }

  /**
   * With a size selected the heart tracks that variant; without one it falls
   * back to "any size of this product is saved" so a returning shopper still
   * sees their save.
   */
  private isSaved(): boolean {
    const variantId = this.selectedVariantId()
    return variantId ? swymService.hasVariant(variantId) : swymService.has(this.productId)
  }

  private syncSaved = (): void => {
    this.setSaved(this.isSaved())
  }

  private onFormChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null
    if (target?.name !== 'id') return
    this.syncSaved()
  }

  /**
   * `data-form-id` matches the `id` shared by the product form host element
   * (`enhanced-product-form`/`gift-card-form`) — and, for those two, its
   * internal real `<form>` — so `getElementById` finds a valid ancestor in every
   * case.
   */
  private formHost(): HTMLElement | null {
    const formId = this.dataset.formId
    return formId ? document.getElementById(formId) : null
  }

  /**
   * variant-selector.ts only propagates main → sticky (it clicks the paired
   * option on the sticky's own `variant-selector` — see its `pairFormId`
   * handling), never the other way, and there's no re-entrancy guard that
   * would make mirroring it back safe. So a shopper who picks a size only in
   * the sticky bar never updates the main form's `[name="id"]`. This falls
   * back to reading the sticky form's own `[name="id"]` in that case. It's a
   * one-off read at click/state-check time, not a live sync: no `change`
   * listener is attached to the sticky form, so `onFormChange` stays the only
   * listener this element owns.
   *
   * Both reads are gated by `formHasResolvedVariant()`: a hidden
   * `select[name="id"]` can already carry a value at page load even when
   * nothing has genuinely been chosen (see that method), so the value alone
   * is not trusted for either form.
   */
  private selectedVariantId(): number | null {
    if (this.formHasResolvedVariant(this.dataset.formId)) {
      const value = this.formHost()?.querySelector<HTMLInputElement | HTMLSelectElement>('[name="id"]')?.value
      if (value) return Number(value)
    }
    if (this.formHasResolvedVariant(this.dataset.pairFormId)) {
      const stickyValue = this.pairFormHost()?.querySelector<HTMLInputElement | HTMLSelectElement>('[name="id"]')?.value
      if (stickyValue) return Number(stickyValue)
    }
    return null
  }

  /**
   * The size gate can otherwise be bypassed: `field-variant-selector-grid.liquid`
   * drives a chip's `active` class off `variant.selected` (line ~99) but the
   * hidden `<option selected>` feeding `[name="id"]` off `value.selected`
   * (line ~192) — two different Liquid objects that can disagree, so the
   * hidden select can already hold a real variant id with no chip showing as
   * selected and "Select Size" still displayed. `field-variant-selector-hidden.liquid`
   * (the sticky bar's selector) has the same split: its `<option value="">`
   * placeholder competes with `value.selected` on the same real options.
   *
   * The reliable signal is the one that already gates the ATC button itself:
   * the form's wrapping `<x-modal data-id="…">` (`_form.liquid` for the main
   * form, `product-sticky-atc.liquid` for the sticky one — both assign their
   * own `id__product_form` to `data-id` on that wrapper) only carries
   * `data-ui="visible"` or `data-ui="disabled"` once Liquid has genuinely
   * resolved a variant (a `?variant=` deep link, or a single-value size
   * option); it's absent entirely otherwise. `"disabled"` (sold out, or
   * pre-release) still counts as resolved — the product owner decided saving
   * a sold-out size is allowed, so this only closes the "nothing chosen yet"
   * bypass, not the sold-out-save path.
   *
   * Ordering dependency: this reads `data-ui` at click time, so it only sees
   * the modal's chip listener having already run. That's safe today because
   * `theme.js` is `defer`red (one script, one execution) and
   * `registerCustomElements.ts` defines `x-modal` before `variant-selector`,
   * so the modal's own chip listener (which sets `data-ui`) attaches and
   * fires before the selector's chip handler dispatches the bubbling
   * `change` this element's `onFormChange` listens to. If that define order
   * were ever reversed, this method would read a stale `data-ui` and the
   * heart could sit filled for a size that was never actually saved,
   * persistently rather than just until the next click.
   */
  private formHasResolvedVariant(formId: string | undefined): boolean {
    if (!formId) return false
    // Resolve from this element upward first (the main form's `<x-modal>` is
    // an ancestor of the toggle), falling back to a document-wide lookup for
    // the sticky pair form, which lives outside the main form entirely.
    // Equivalent today either way — the id is unique on the page — but this
    // ordering would still resolve correctly if a page ever rendered two
    // product forms.
    const modal =
      this.closest<HTMLElement>(`x-modal[data-id="${formId}"]`) ??
      document.querySelector<HTMLElement>(`x-modal[data-id="${formId}"]`)
    return modal?.dataset.ui === 'visible' || modal?.dataset.ui === 'disabled'
  }

  /**
   * The sticky ATC form's host element, identified by `data-pair-form-id`
   * (set by `_form.liquid` next to the wishlist render — see that file for
   * why it's computed fresh there rather than reused from `data_pair_form`).
   * Not every template renders a sticky bar, so a missing attribute or a
   * missing element are both silent no-ops.
   */
  private pairFormHost(): HTMLElement | null {
    const pairFormId = this.dataset.pairFormId
    return pairFormId ? document.getElementById(pairFormId) : null
  }

  private productUrl(): string {
    return this.dataset.productUrl ?? window.location.href.split('?')[0]
  }

  /**
   * Reuses the ATC "Select Size" warning (`x-modal[data-id="wishlist-message"]`
   * in blocks/product-page/_form.liquid) rather than adding a second message.
   * `handleShow()` is VisibilityElement's public API. A `data-modal-role="open"`
   * attribute on the heart can't be used instead: it would fire on every click,
   * including when a size IS selected. No hide logic is needed either — that
   * modal carries `group-data-visible/form:hidden`, so choosing a size hides it.
   */
  private showSizePrompt(): void {
    const modal = document.querySelector<HTMLElement & { handleShow?:() => void }>(
      'x-modal[data-id="wishlist-message"]'
    )
    if (!modal) return
    if (typeof modal.handleShow === 'function') modal.handleShow()
    else modal.setAttribute('data-ui', 'visible')
  }

  /**
   * `[data-id="wishlist-status"]` lives alongside the ATC buttons inside the
   * same product-form host (`data-form-id`), not inside this element.
   */
  private statusElement(): HTMLElement | null {
    return this.formHost()?.querySelector<HTMLElement>('[data-id="wishlist-status"]') ?? null
  }

  private showStatus(status: 'success' | 'error'): void {
    const el = this.statusElement()
    if (!el) return
    const message = status === 'success' ? el.dataset.wishlistSuccessMessage : el.dataset.wishlistErrorMessage
    const messageEl = el.querySelector('[data-wishlist-status-message]')
    if (messageEl) messageEl.textContent = message ?? ''
    el.setAttribute('data-status', status)
    el.setAttribute('data-ui', 'visible')
  }

  private hideStatus(): void {
    const el = this.statusElement()
    if (!el) return
    el.removeAttribute('data-ui')
    el.removeAttribute('data-status')
  }

  private setSaved(saved: boolean): void {
    if (saved) {
      this.button?.setAttribute('data-saved', '')
      this.button?.setAttribute('aria-label', 'Remove from Wishlist')
    } else {
      this.button?.removeAttribute('data-saved')
      this.button?.setAttribute('aria-label', 'Add to Wishlist')
    }
  }
}
